import { computed } from 'vue';
import { useStore } from 'vuex';

import { SELF_PACED_COURSE_KEY } from '@/constants/CourseTypes.js';
import { initialState } from '@/Helpers.js';

/**
 * Shared helper: returns whether a course or course-type object represents a self-paced course.
 * Use this outside components (e.g. route guards) to avoid duplicating logic.
 *
 * @param {{ course_type?: { title?: string }, course_type_id?: number } | null | undefined} courseOrCourseType - Course entity, or course_type from store
 * @returns {boolean}
 */
export function isCourseSelfPaced(courseOrCourseType) {
    if (!courseOrCourseType) return false;
    if (courseOrCourseType.title === SELF_PACED_COURSE_KEY) return true;
    if (courseOrCourseType.course_type?.title === SELF_PACED_COURSE_KEY) return true;
    if (courseOrCourseType.course_type_id != null) {
        const courseTypes = initialState('CourseTypes') ?? [];
        const type = courseTypes.find(t => t.id === courseOrCourseType.course_type_id);
        return type?.title === SELF_PACED_COURSE_KEY;
    }
    return false;
}

/**
 * Composable to determine if the current course is self-paced.
 * Uses CourseTemplateForm store when no course is passed; supports an optional course ref
 * for pages (e.g. dashboard, gradebook, menu) where the form is not hydrated.
 *
 * @param {Object} [options]
 * @param {import('vue').Ref<{ course_type?: { title?: string }, course_type_id?: number } | null | undefined>} [options.course]
 * @returns {{ isSelfPacedCourse: import('vue').ComputedRef<boolean> }}
 */
export function useSelfPacedCourse(options = {}) {
    const store = useStore();
    const courseRef = options.course;

    const isSelfPacedCourse = computed(() => {
        if (courseRef?.value) {
            return isCourseSelfPaced(courseRef.value);
        }
        const courseItem = store.getters['CourseTemplateForm/courseItem'];
        const courseType = courseItem('course_type');
        if (courseType) {
            return isCourseSelfPaced(courseType);
        }
        const courseTypeId = courseItem('course_type_id');
        return isCourseSelfPaced(courseTypeId != null ? { course_type_id: courseTypeId } : null);
    });

    return { isSelfPacedCourse };
}

export default useSelfPacedCourse;
